import {waypointFixedLayerIds} from '@gaia/utils/gaia/getSyncMapboxStyle/fixedLayerIds';
import type {
    MapCursor,
    UseInteractionHandlersOptions,
} from '@gaia/utils/gaia/useInteractionHandlers';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import type mapboxgl from 'mapbox-gl';

const embeddedWaypointLayerId = waypointFixedLayerIds.embeddedMap;

function onInteractionChangeSafe(
    interaction: InteractionState,
    nextInteraction: InteractionState,
    onInteractionChange: React.Dispatch<React.SetStateAction<InteractionState>>
) {
    if (!isEqual(interaction, nextInteraction)) {
        onInteractionChange(nextInteraction);
    }
}

/*
 * The only supported interaction in embedded maps are clicking on waypoint popovers.
 */
export const embedded = {
    getCursor: (interaction: NormalInteractionState): MapCursor => {
        if (interaction.subtype === 'hovering') {
            return 'pointer';
        } else {
            return 'default';
        }
    },

    onMouseDown: () => {},

    onMouseUp: () => {},

    onMouseMove: throttle(
        (
            event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
            {
                onInteractionChange,
                interaction,
            }: UseInteractionHandlersOptions<NormalInteractionState>
        ) => {
            const bboxOffset = 5;
            const queriedFeatures = event.target
                .queryRenderedFeatures([
                    [event.point.x - bboxOffset, event.point.y + bboxOffset],
                    [event.point.x + bboxOffset, event.point.y - bboxOffset],
                ])
                .map((f: any) => f.toJSON());

            const feature = queriedFeatures.find((f) => embeddedWaypointLayerId === f.layer.id);

            if (feature) {
                onInteractionChangeSafe(
                    interaction,
                    {
                        type: 'normal',
                        subtype: 'hovering',
                        feature,
                    },
                    onInteractionChange
                );
            } else {
                onInteractionChangeSafe(
                    interaction,
                    {type: 'normal', subtype: 'normal'},
                    onInteractionChange
                );
            }
        },
        200,
        {leading: true, trailing: true}
    ),

    onMouseOut: () => {},

    onClick: (
        _event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        _options: UseInteractionHandlersOptions<NormalInteractionState>
    ) => {},

    onDblClick: () => {},
};
